import type { OfferView, RewardsResponse } from '@xchange/shared';
import type { ActivityItem, PairView } from './api.js';
import { amountText } from './assets.js';

/**
 * Whether a bid being typed will earn the bid share of today's XGE rewards,
 * worked out from the book the app already holds: no request of its own.
 *
 * The API's rule (services/rewards.ts, `usdcInBids`): a USDC bid counts while
 * it waits when it holds at least the minimum, and its price is no more than
 * the band below the market. The market is the higher of the lowest offer by
 * someone else that itself holds the minimum, and the week's trades. A bid
 * that fills at once never waits, so it is a trade, which earns from the
 * traders' half instead.
 */
export type BidReward =
  /** All of it fills against offers now: a trade. */
  | { kind: 'fills' }
  /** What waits counts toward the bid share. */
  | { kind: 'earns' }
  /** What waits is under the minimum; `partly` when some of it fills first. */
  | { kind: 'small'; partly: boolean }
  /** Too far below the market; `from` is the least price that counts. */
  | { kind: 'low'; from: bigint; against: 'offer' | 'trades'; partly: boolean }
  /** No offer of the minimum and no trades this week: nothing to measure against. */
  | { kind: 'none' };

export type BidRules = Pick<
  RewardsResponse,
  'active' | 'liquidityPercent' | 'liquidityBandPercent' | 'liquidityMinUsd'
>;

type BookOffer = Pick<OfferView, 'pair' | 'side' | 'price' | 'remaining' | 'mine' | 'partial'>;
type Trade = Pick<ActivityItem, 'pair' | 'kind' | 'baseAmount' | 'quoteAmount' | 'at'>;

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/** Null when there is nothing to say: not a USDC bid, nothing typed, or the share is off. */
export function bidReward(args: {
  pair: Pick<PairView, 'name' | 'quote' | 'priceUnit'>;
  /** The amount typed, in the base coin's smallest units. */
  base: bigint;
  /** The price typed, in the pair's stored unit. */
  price: bigint;
  open: readonly BookOffer[];
  trades: readonly Trade[];
  rules: BidRules | null;
  now: Date;
}): BidReward | null {
  const { pair, base, price, rules } = args;
  if (rules === null || !rules.active || !(rules.liquidityPercent > 0)) return null;
  if (pair.quote !== 'USDC' || base <= 0n || price <= 0n) return null;
  const unit = BigInt(pair.priceUnit);
  const min = BigInt(rules.liquidityMinUsd);

  // Offers by someone else, lowest first, as the bid would meet them.
  const asks = args.open
    .filter((o) => o.pair === pair.name && o.side === 'sell' && o.mine !== 'maker')
    .map((o) => ({ price: BigInt(o.price), remaining: BigInt(o.remaining), partial: o.partial }))
    .sort((a, b) => (a.price < b.price ? -1 : a.price > b.price ? 1 : 0));
  let left = base;
  for (const a of asks) {
    if (left === 0n || a.price > price) break;
    if (!a.partial && a.remaining > left) continue;
    const take = a.remaining < left ? a.remaining : left;
    a.remaining -= take;
    left -= take;
  }
  if (left === 0n) return { kind: 'fills' };
  if ((left * price) / unit < min) return { kind: 'small', partly: left < base };

  // The market it is measured against, once the part that fills has filled.
  let ask: bigint | null = null;
  for (const a of asks)
    if ((a.remaining * a.price) / unit >= min && (ask === null || a.price < ask)) ask = a.price;
  const since = args.now.getTime() - WEEK_MS;
  let quote = 0n;
  let traded = 0n;
  for (const t of args.trades) {
    if (t.pair !== pair.name || t.kind !== 'trade' || Date.parse(t.at) < since) continue;
    quote += BigInt(t.quoteAmount);
    traded += BigInt(t.baseAmount);
  }
  const week = traded === 0n ? 0n : (quote * unit) / traded;
  const market = ask !== null && ask >= week ? ask : week;
  if (market === 0n) return { kind: 'none' };
  const from = (market * BigInt(100 - rules.liquidityBandPercent)) / 100n;
  if (price < from)
    return {
      kind: 'low',
      from,
      against: ask !== null && ask >= week ? 'offer' : 'trades',
      partly: left < base,
    };
  return { kind: 'earns' };
}

/** "$10", not "$10.00". */
const dollars = (minor: bigint): string => amountText('USDC', minor).replace(/\.00$/, '');

/**
 * The sentence under the ticket. Plain words: never a rate or an amount of
 * XGE. `simple` for Simple mode, where the price is the book's and cannot
 * be typed, so a better price is found in Advanced.
 */
export function bidRewardText(
  r: BidReward,
  rules: BidRules,
  priceText: (price: bigint) => string,
  simple = false,
): string {
  const min = dollars(BigInt(rules.liquidityMinUsd));
  switch (r.kind) {
    case 'fills':
      return "This fills at once. Every swap earns a share of today's XGE rewards.";
    case 'earns':
      return "This bid earns a share of today's XGE rewards while it waits.";
    case 'small':
      return `Bids of ${min} or more earn a share of today's XGE rewards while they wait. ${r.partly ? 'The part left waiting is' : 'This one is'} under ${min}.`;
    case 'low': {
      const what = r.partly ? 'The part left waiting is' : 'This bid is';
      const below = r.against === 'offer' ? 'the lowest offer' : "this week's trades";
      const fix = simple
        ? 'In Advanced you can name your own price.'
        : `From ${priceText(r.from)} each, it would.`;
      return `${what} more than ${rules.liquidityBandPercent}% below ${below}, so it earns no XGE rewards while it waits. ${fix}`;
    }
    case 'none':
      return `Bids earn a share of today's XGE rewards once someone offers ${min} or more to measure them against.`;
  }
}
